/**
 * The arithmetic of the Özet line charts (v0.1.266): the period and the one before it laid over each other by day
 * index (day 1 with day 1, whatever the dates), running sums, the honest comparison over the days both have, and the
 * SVG path of a line that leaves a gap where a day has no value. Pure functions, so the tests can name numbers.
 */

import type { OzetRange, OzetSummaryDay } from '../api/ozet.js';

export interface AlignedPoint {
  /** 0 for the first day of each period. */
  index: number;
  currentDay: string | null;
  current: number | null;
  previousDay: string | null;
  previous: number | null;
}

function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * The current and the previous period side by side by day index. The length is the longer of the two (a month's
 * 31st has no 31st in February); a day outside a period or missing from `daily` is null on that side.
 */
export function alignByDayIndex(
  daily: readonly OzetSummaryDay[],
  current: OzetRange,
  previous: OzetRange,
  pick: (day: OzetSummaryDay) => number | null,
): AlignedPoint[] {
  const byDay = new Map(daily.map((day) => [day.day, day]));
  const length = Math.max(current.days, previous.days);
  const out: AlignedPoint[] = [];
  for (let index = 0; index < length; index += 1) {
    const currentDay = index < current.days ? addDays(current.from, index) : null;
    const previousDay = index < previous.days ? addDays(previous.from, index) : null;
    const now = currentDay === null ? undefined : byDay.get(currentDay);
    const before = previousDay === null ? undefined : byDay.get(previousDay);
    out.push({
      index,
      currentDay,
      current: now === undefined ? null : pick(now),
      previousDay,
      previous: before === undefined ? null : pick(before),
    });
  }
  return out;
}

/** Running sums; a day without a value stays null (a gap) and the sum goes on after it. */
export function cumulative(values: readonly (number | null)[]): (number | null)[] {
  let total = 0;
  return values.map((value) => {
    if (value === null) return null;
    total += value;
    return total;
  });
}

/**
 * Both sums over the days the current period has a value for, so a late source (GA4 comes the next morning) or the
 * days still to come never read as a drop: "this period" against "the same days of the previous one". `days` is how
 * many days were compared; previous is null when the previous period has no value on any of them.
 */
export function comparedSums(points: readonly AlignedPoint[]): {
  current: number | null;
  previous: number | null;
  days: number;
} {
  let current: number | null = null;
  let previous: number | null = null;
  let days = 0;
  for (const point of points) {
    if (point.current === null) continue;
    days += 1;
    current = (current ?? 0) + point.current;
    if (point.previous !== null) previous = (previous ?? 0) + point.previous;
  }
  return { current, previous, days };
}

/** The x of a point in a plot `width` wide with `count` points: the middle of its slot (as BarValueLabels places). */
export function pointX(index: number, count: number, width: number): number {
  return ((index + 0.5) / Math.max(1, count)) * width;
}

/** The y of a value in a plot `height` high, 0 on the base line (as the bars of ozet-charts.tsx). */
export function pointY(value: number, max: number, height: number): number {
  return height - 1 - (Math.max(0, value) / Math.max(1, max)) * (height - 4);
}

/** An SVG path through the values; a null breaks the line, so a day without data is a gap, never a 0. */
export function linePath(values: readonly (number | null)[], max: number, width: number, height: number): string {
  const parts: string[] = [];
  let pen = false;
  values.forEach((value, index) => {
    if (value === null) {
      pen = false;
      return;
    }
    parts.push(
      `${pen ? 'L' : 'M'}${pointX(index, values.length, width).toFixed(1)} ${pointY(value, max, height).toFixed(1)}`,
    );
    pen = true;
  });
  return parts.join(' ');
}

/** The points with no neighbour on either side: a path cannot show them, so the chart draws a dot. */
export function isolatedPoints(values: readonly (number | null)[]): number[] {
  const out: number[] = [];
  values.forEach((value, index) => {
    if (value === null) return;
    const before = index > 0 ? values[index - 1] : null;
    const after = index < values.length - 1 ? values[index + 1] : null;
    if ((before === null || before === undefined) && (after === null || after === undefined)) out.push(index);
  });
  return out;
}

/** The highest value of several series (nulls left out), at least 1 so a flat zero line sits on the base. */
export function maxOf(...series: readonly (readonly (number | null)[])[]): number {
  let max = 1;
  for (const values of series) for (const value of values) if (value !== null && value > max) max = value;
  return max;
}

/** The `count` groups with the most in total, biggest first (ties in the given order); for the default chips. */
export function biggest<K extends string>(keys: readonly K[], total: (key: K) => number, count: number): K[] {
  return keys
    .map((key, order) => ({ key, order, total: total(key) }))
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total || a.order - b.order)
    .slice(0, count)
    .map((item) => item.key);
}
